<script setup lang="ts">
import type { Attachment, Content } from '~/types/blog'
import { normalizeFieldText } from '~/composables/useCustomFields'
import { resolveAssetUrl, resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { getAttachmentName, isImageAttachment } from '~/utils/attachments'
import { isAppleSafari, resolveAlbumLightboxURL, resolveAlbumMediaSources, selectAlbumMediaSources } from '~/utils/albumMedia.mjs'
import {
  resolveCommentIdFromHash,
  scrollToElementWithOffset,
  waitForAnchorNodeByHash
} from '~/composables/useAnchorScroll'

const route = useRoute()
const blogApi = useBlogApi()
const siteData = await useSiteData()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const pageDesc = normalizeFieldText(siteData?.setting?.customFields, 'tweetPageDescription', '简短记录与状态更新。')
const gravatarMirror = normalizeFieldText(siteData?.setting?.customFields, 'gravatarMirror', 'https://www.gravatar.com/avatar')
const pageSize = 12

const { data: firstPage } = await useAsyncData('tweets:first-page', () =>
  blogApi.getContentList({ page: 1, pageSize, type: 'tweet' })
)

const list = ref<Content[]>([])
const total = ref(0)
const nextPage = ref(2)
const loadingMore = ref(false)
const sentinel = ref<HTMLElement | null>(null)
const anchorTweetContentId = ref(0)
const tweetLiked = reactive<Record<number, boolean>>({})
const tweetLikeEffectTokens = reactive<Record<number, number>>({})
const contentLike = useOptimisticLike('content', blogApi.likeContent)
const likePersistenceReady = ref(false)
const preferOriginalHDR = ref(false)
let observer: IntersectionObserver | null = null
let tweetAnchorRequestID = 0
const {
  visible: tweetLightboxVisible,
  sources: tweetLightboxSources,
  index: tweetLightboxIndex,
  open: openTweetLightboxImages,
  close: closeTweetLightbox
} = useImageLightbox()

const imageList = (item: Content) => (item.attachments || []).filter(isImageAttachment)
const fileList = (item: Content) => (item.attachments || []).filter(attachment => !isImageAttachment(attachment))
const imageMediaSources = (image: Attachment) => selectAlbumMediaSources(resolveAlbumMediaSources(image), preferOriginalHDR.value)
const imageFallbackURL = (image: Attachment) => {
  const sources = imageMediaSources(image)
  return resolveImageAssetUrl(sources.fallbackURL || sources.displayURL)
}
const imageSources = (item: Content) =>
  imageList(item)
    .map((image, imageIndex) => {
      const src = resolveImageAssetUrl(resolveAlbumLightboxURL(image, preferOriginalHDR.value))
      if (!src) {
        return null
      }
      const title = getAttachmentName(image)
      return { imageIndex, src, fallbackSrc: imageFallbackURL(image), title, alt: title }
    })
    .filter((image): image is { imageIndex: number; src: string; fallbackSrc: string; title: string; alt: string } => Boolean(image))
const hasMore = computed(() => list.value.length < total.value)

const syncTweetLikedState = (items: Content[] = list.value) => {
  if (!likePersistenceReady.value) {
    return
  }
  items.forEach(item => {
    const id = Number(item.ID || 0)
    if (id > 0 && contentLike.isLiked(item)) {
      tweetLiked[id] = true
    }
  })
}

const resetList = () => {
  list.value = firstPage.value?.list || []
  total.value = firstPage.value?.total || 0
  nextPage.value = 2
  Object.keys(tweetLiked).forEach(key => delete tweetLiked[Number(key)])
  Object.keys(tweetLikeEffectTokens).forEach(key => delete tweetLikeEffectTokens[Number(key)])
  syncTweetLikedState()
}

watch(
  () => firstPage.value,
  () => resetList(),
  { immediate: true }
)

// openTweetLightbox 跳过无可用预览的图片，保持点击图片与灯箱索引一致
const openTweetLightbox = (item: Content, imageIndex: number) => {
  const sources = imageSources(item)
  const sourceIndex = sources.findIndex(source => source.imageIndex === imageIndex)
  if (sourceIndex < 0) return
  openTweetLightboxImages(sources, sourceIndex)
}

const triggerTweetLikeEffect = (id: number) => {
  tweetLikeEffectTokens[id] = Number(tweetLikeEffectTokens[id] || 0) + 1
}

const loadMore = async () => {
  if (loadingMore.value || !hasMore.value) {
    return
  }
  loadingMore.value = true
  try {
    const data = await blogApi.getContentList({ page: nextPage.value, pageSize, type: 'tweet' })
    const nextList = data.list || []
    list.value = [...list.value, ...nextList]
    syncTweetLikedState(nextList)
    total.value = data.total || total.value
    nextPage.value += 1
  } finally {
    loadingMore.value = false
  }
}

const waitForTweetLoading = async () => {
  while (loadingMore.value) {
    await new Promise(resolve => window.setTimeout(resolve, 80))
  }
}

const findTweetItem = (id: number) => list.value.find(item => Number(item.ID || 0) === id)

const ensureTweetItemLoaded = async (id: number) => {
  await nextTick()
  let item = findTweetItem(id)
  while (!item && hasMore.value) {
    await waitForTweetLoading()
    await loadMore()
    await nextTick()
    item = findTweetItem(id)
  }
  return item || null
}

const ensureTweetCommentAnchorVisible = async (hash: string, behavior: ScrollBehavior = 'smooth') => {
  if (!import.meta.client) {
    return
  }
  const requestID = (tweetAnchorRequestID += 1)
  const commentId = resolveCommentIdFromHash(hash)
  if (commentId <= 0) {
    anchorTweetContentId.value = 0
    return
  }
  anchorTweetContentId.value = 0

  const anchor = await blogApi.getCommentAnchor(0, commentId)
  if (requestID !== tweetAnchorRequestID) {
    return
  }
  const contentId = Number(anchor?.contentId || 0)
  if (!anchor?.found || contentId <= 0) {
    return
  }
  const content = await blogApi.getContent(contentId)
  if (requestID !== tweetAnchorRequestID || content?.type !== 'tweet') {
    return
  }
  const item = await ensureTweetItemLoaded(contentId)
  if (requestID !== tweetAnchorRequestID || !item) {
    return
  }
  anchorTweetContentId.value = contentId
  const tweetHash = `#tweet-${contentId}`
  if (await waitForAnchorNodeByHash(tweetHash, 12, 80)) {
    scrollToElementWithOffset(document.getElementById(`tweet-${contentId}`), behavior)
  }
}

const likeTweet = (item: Content) => {
  const id = Number(item.ID || 0)
  contentLike.like(item, () => {
    tweetLiked[id] = true
    triggerTweetLikeEffect(id)
  })
}

const onTweetCommentSubmitted = (item: Content) => {
  item.commentCount = Number(item.commentCount || 0) + 1
}

const bindObserver = () => {
  if (!import.meta.client) return
  observer?.disconnect()
  observer = null
  if (!sentinel.value || !hasMore.value) {
    return
  }
  observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) {
        void loadMore()
      }
    },
    { rootMargin: '260px 0px' }
  )
  observer.observe(sentinel.value)
}

onMounted(() => {
  likePersistenceReady.value = true
  preferOriginalHDR.value = isAppleSafari(navigator.userAgent, navigator.vendor)
  syncTweetLikedState()
  bindObserver()
})
watch([sentinel, hasMore], () => bindObserver())

watch(
  () => route.hash,
  hash => {
    void ensureTweetCommentAnchorVisible(hash)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  observer?.disconnect()
})

useSeoMeta({
  title: `说说 - ${siteName}`,
  description: pageDesc,
  ogTitle: `说说 - ${siteName}`,
  ogDescription: pageDesc
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <section class="tweet-page">
    <div class="title card">
      <h1>说说</h1>
      <p>{{ pageDesc }}</p>
    </div>

    <div v-if="list.length" class="timeline">
      <article v-for="item in list" :id="`tweet-${item.ID}`" :key="item.ID" class="timeline-item">
        <div class="timeline-dot" />
        <div class="card timeline-card">
          <p class="content">{{ item.content || '-' }}</p>

          <div v-if="imageList(item).length" class="thumb-grid">
            <button
              v-for="(image, index) in imageList(item)"
              :key="image.url"
              class="thumb"
              type="button"
              :title="getAttachmentName(image)"
              @click="openTweetLightbox(item, index)"
            >
              <BlogMediaImage
                :src="imageFallbackURL(image)"
                :fallback-src="imageFallbackURL(image)"
                :sources="imageMediaSources(image).hdrSources.map(source => ({ ...source, url: resolveImageAssetUrl(source.url) }))"
                :alt="getAttachmentName(image)"
              />
              <span v-if="image.isHDR" class="thumb-hdr">HDR</span>
            </button>
          </div>

          <div v-if="fileList(item).length" class="attachment-list">
            <a
              v-for="file in fileList(item)"
              :key="file.url"
              :href="resolveAssetUrl(file.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="attachment-link"
            >
              <i class="fa-regular fa-file-lines" aria-hidden="true" />
              <span>{{ getAttachmentName(file) }}</span>
            </a>
          </div>

          <BlogTweetComments
            v-slot="{ expanded: commentsExpanded, commentCount, canToggle, loading: commentsLoading, toggleExpanded }"
            :content-id="item.ID"
            :comment-count="item.commentCount || 0"
            :allow-comment="item.allowComment !== false"
            :gravatar-mirror="gravatarMirror"
            :auto-expand="anchorTweetContentId === item.ID"
            @submitted="onTweetCommentSubmitted(item)"
          >
            <div class="meta">
              <span><i class="fa-regular fa-user" aria-hidden="true" />{{ item.authorName || '佚名' }}</span>
              <span><i class="fa-regular fa-clock" aria-hidden="true" />{{ item.publishedAt ? new Date(item.publishedAt).toLocaleString('zh-CN') : '未发布' }}</span>
              <div class="tweet-actions">
                <BlogLikeButton
                  variant="simple"
                  :liked="tweetLiked[item.ID]"
                  :count="item.likeCount || 0"
                  :effect-token="tweetLikeEffectTokens[item.ID] || 0"
                  @click="likeTweet(item)"
                />
                <button
                  type="button"
                  class="tweet-comment-btn"
                  :class="{ active: commentsExpanded, loading: commentsLoading }"
                  :disabled="!canToggle"
                  :aria-busy="commentsLoading"
                  @click="!commentsLoading && toggleExpanded()"
                >
                  <BlogLoadingIndicator v-if="commentsLoading" :show-text="false" />
                  <i v-else class="fa-regular fa-comment-dots" aria-hidden="true" />
                  {{ commentCount || 0 }}
                </button>
              </div>
            </div>
          </BlogTweetComments>
        </div>
      </article>

      <div class="load-footer">
        <div ref="sentinel" class="scroll-sentinel" />
        <BlogLoadingIndicator v-if="loadingMore" />
        <BlogListEnd v-else-if="!hasMore" />
      </div>
    </div>

    <div v-else class="empty-card">暂无说说</div>
    <BlogEasyLightbox
      :visible="tweetLightboxVisible"
      :sources="tweetLightboxSources"
      :index="tweetLightboxIndex"
      @hide="closeTweetLightbox"
    />
  </section>
</template>

<style scoped>
.tweet-page {
  display: grid;
  gap: 14px;
}

.title {
  padding: 20px;
}

h1 {
  font-size: var(--heading-title-size);
}

.title p {
  margin-top: 8px;
  color: var(--text-soft);
}

.timeline {
  position: relative;
  display: grid;
  gap: 14px;
  padding-left: 0;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 12px;
  bottom: 12px;
  width: 1px;
  background: var(--line);
}

.timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 10px;
  align-items: start;
}

.timeline-dot {
  justify-self: center;
  margin-top: 10px;
  width: 14px;
  height: 14px;
  border: 2px solid var(--brand);
  border-radius: 999px;
  background: var(--bg);
  z-index: 1;
}

.timeline-card {
  padding: 14px;
}

.content {
  white-space: pre-wrap;
  line-height: 1.8;
}

.thumb-grid {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.thumb {
  position: relative;
  display: block;
  width: 60px;
  height: 60px;
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  padding: 0;
  cursor: pointer;
  background: transparent;
}

.thumb :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb picture {
  display: block;
  width: 100%;
  height: 100%;
}

.thumb-hdr {
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  padding: 1px 3px;
  color: rgba(255, 255, 255, 0.92);
  background: rgba(10, 16, 24, 0.38);
  border: 1px solid rgba(255, 255, 255, 0.24);
  font-size: 8px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.04em;
}

.attachment-list {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--text);
  text-decoration: none;
  font-size: 13px;
}

.attachment-link i {
  font-size: 15px;
  color: var(--text-soft);
}

.meta {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: var(--text-soft);
}

.meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.tweet-actions {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.tweet-actions :deep(.like-button.variant-simple) {
  padding-inline: 6px;
}

.tweet-comment-btn {
  border: 0;
  border-radius: 999px;
  padding: 5px 6px;
  color: var(--text-soft);
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.16s ease;
}

.tweet-comment-btn i {
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.tweet-comment-btn.active {
  color: var(--brand);
}

.tweet-comment-btn.loading {
  opacity: 0.72;
}

.tweet-comment-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.scroll-sentinel {
  position: absolute;
  inset: 0 0 auto;
  height: 1px;
}

.load-footer {
  position: relative;
  min-height: 1px;
}

@media (max-width: 760px) {
  .thumb {
    width: 54px;
    height: 54px;
  }
}

</style>
